import "./AttachmentManager.scss";
import { useEffect, useState } from "react";
import {
  Button,
  FileButton,
  Text,
  Group,
  Stack,
  ActionIcon,
  Alert,
  Loader,
  Paper,
} from "@mantine/core";
import {
  IconPaperclip,
  IconTrash,
  IconAlertCircle,
  IconFileTypePdf,
  IconChevronUp,
  IconChevronDown,
} from "@tabler/icons-react";
import { useEngagementLetterStore } from "@/utils/stores/engagementLetterStore";
import type { EngagementLetterAttachment } from "@/types/engagement";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_TOTAL_SIZE = 50 * 1024 * 1024; // 50MB

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const AttachmentManager = () => {
  const {
    template,
    attachments,
    isLoadingAttachments,
    isUploadingAttachment,
    attachmentError,
    fetchAttachments,
    uploadAttachmentFile,
    deleteAttachmentById,
    reorderAttachments,
  } = useEngagementLetterStore();

  const [localError, setLocalError] = useState<string | null>(null);

  // Fetch attachments when template is loaded
  useEffect(() => {
    if (template) {
      fetchAttachments();
    }
  }, [template, fetchAttachments]);

  // Calculate total size
  const totalSize = attachments.reduce((sum, att) => sum + att.fileSize, 0);

  const handleFileSelect = async (file: File | null) => {
    if (!file) return;

    setLocalError(null);

    // Validate file type
    if (file.type !== "application/pdf") {
      setLocalError("Only PDF files are allowed");
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setLocalError(
        `File size must be less than ${formatFileSize(MAX_FILE_SIZE)}`
      );
      return;
    }

    // Validate total size
    if (totalSize + file.size > MAX_TOTAL_SIZE) {
      setLocalError(
        `Total attachments size would exceed ${formatFileSize(MAX_TOTAL_SIZE)}`
      );
      return;
    }

    try {
      await uploadAttachmentFile(file);
    } catch (error) {
      setLocalError("Failed to upload attachment. Please try again.");
    }
  };

  const handleDelete = async (attachmentId: string) => {
    if (!confirm("Are you sure you want to delete this attachment?")) {
      return;
    }

    try {
      await deleteAttachmentById(attachmentId);
    } catch (error) {
      setLocalError("Failed to delete attachment. Please try again.");
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return; // Can't move first item up

    const newAttachments = [...attachments];
    [newAttachments[index - 1], newAttachments[index]] = [
      newAttachments[index],
      newAttachments[index - 1],
    ];

    try {
      await reorderAttachments(newAttachments.map((a) => a.id));
    } catch (error) {
      setLocalError("Failed to reorder attachments. Please try again.");
    }
  };

  const handleMoveDown = async (index: number) => {
    if (index === attachments.length - 1) return; // Can't move last item down

    const newAttachments = [...attachments];
    [newAttachments[index], newAttachments[index + 1]] = [
      newAttachments[index + 1],
      newAttachments[index],
    ];

    try {
      await reorderAttachments(newAttachments.map((a) => a.id));
    } catch (error) {
      setLocalError("Failed to reorder attachments. Please try again.");
    }
  };

  const displayError = localError || attachmentError;

  return (
    <div className="attachment-manager">
      <div className="attachment-manager-header">
        <Text fw={500}>Attachments (Optional)</Text>
        <Text size="sm" c="dimmed">
          Add PDF documents that will be appended to the end of your engagement
          letter
        </Text>
      </div>

      {displayError && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          title="Error"
          mb="md"
          withCloseButton
          onClose={() => setLocalError(null)}
        >
          {displayError}
        </Alert>
      )}

      <div className="attachment-manager-content">
        {isLoadingAttachments ? (
          <Paper p="md" withBorder>
            <Group gap="sm">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">
                Loading attachments...
              </Text>
            </Group>
          </Paper>
        ) : (
          <Stack gap="sm">
            {attachments.length > 0 ? (
              <>
                {attachments.map(
                  (attachment: EngagementLetterAttachment, index: number) => (
                    <Paper
                      key={attachment.id}
                      p="sm"
                      withBorder
                      className="attachment-item"
                    >
                      <Group justify="space-between" wrap="nowrap">
                        <Group gap="sm" wrap="nowrap">
                          <IconFileTypePdf size={24} color="#d32f2f" />
                          <div className="attachment-info">
                            <Text size="sm" fw={500} lineClamp={1}>
                              {attachment.fileName}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {formatFileSize(attachment.fileSize)} • Position{" "}
                              {index + 1}
                            </Text>
                          </div>
                        </Group>
                        <Group gap="xs" wrap="nowrap">
                          <ActionIcon
                            variant="subtle"
                            onClick={() => handleMoveUp(index)}
                            disabled={index === 0}
                            title="Move up"
                          >
                            <IconChevronUp size={18} />
                          </ActionIcon>
                          <ActionIcon
                            variant="subtle"
                            onClick={() => handleMoveDown(index)}
                            disabled={index === attachments.length - 1}
                            title="Move down"
                          >
                            <IconChevronDown size={18} />
                          </ActionIcon>
                          <ActionIcon
                            color="red"
                            variant="subtle"
                            onClick={() => handleDelete(attachment.id)}
                            title="Delete attachment"
                          >
                            <IconTrash size={18} />
                          </ActionIcon>
                        </Group>
                      </Group>
                    </Paper>
                  )
                )}
                <Text size="xs" c="dimmed" ta="right">
                  Total size: {formatFileSize(totalSize)} /{" "}
                  {formatFileSize(MAX_TOTAL_SIZE)}
                </Text>
              </>
            ) : (
              <Paper p="md" withBorder>
                <Text size="sm" c="dimmed" ta="center">
                  No attachments added yet
                </Text>
              </Paper>
            )}
          </Stack>
        )}

        <FileButton onChange={handleFileSelect} accept="application/pdf">
          {(props) => (
            <Button
              {...props}
              leftSection={<IconPaperclip size={18} />}
              variant="light"
              fullWidth
              disabled={isUploadingAttachment || totalSize >= MAX_TOTAL_SIZE}
              loading={isUploadingAttachment}
            >
              {isUploadingAttachment
                ? "Uploading..."
                : totalSize >= MAX_TOTAL_SIZE
                  ? "Maximum size reached"
                  : "Add PDF Attachment"}
            </Button>
          )}
        </FileButton>

        <Text size="xs" c="dimmed">
          • Max file size: {formatFileSize(MAX_FILE_SIZE)}
          <br />• Max total size: {formatFileSize(MAX_TOTAL_SIZE)}
          <br />• Attachments will appear at the end of the engagement letter
        </Text>
      </div>
    </div>
  );
};

export default AttachmentManager;
